import React, {useEffect, useState} from 'react';
import {cn} from '@bem-react/classname';
import {IClassNameProps} from '@bem-react/core';
import './CreateObjectOptions.css';
import {useTranslation} from 'react-i18next';
import {useEffectDidMount} from '../../../hooks/useEffectDidMount';
import {ICreateObjectData} from '../../../../common-code/interfaces/createData/ICreateObjectData';
import {KitchenService} from '../../../3d/services/KitchenService/KitchenService';
import useCreateOptions from '../../../hooks/useCreateOptions';
import {IModulePriceData} from '../../../../common-code/interfaces/catalog/IModulePriceData';
import {Svg} from '../../components/Svg';
import {Button} from '../../components/Button';
import {CreateObjectOptionsPriceAmount} from './PriceAmount/CreateObjectOptions-PriceAmount';
import {CreateObjectOptionsList} from './List/CreateObjectOptions-List';
import {showMessage} from '../../../helpers';
import {MESSAGE_TYPE_ERROR, MESSAGE_TYPE_WARNING} from '../../../constants';
import {useDispatch} from 'react-redux';
import {ThreeUnit} from '../../../3d/objects/threeD/ThreeUnit/ThreeUnit';
import {TCreateObjectError} from '../../../../common-code/types/createObject/TCreateObjectError';

export const cnCreateObjectOptions = cn('CreateObjectOptions');

export interface ICreateObjectOptionsProps extends IClassNameProps {
    objectData: ICreateObjectData;
    width: string | null;
    corpusWidth: string | null;
    service: KitchenService;
    onClose: (value: boolean) => void;
}

export const CreateObjectOptions: React.FC<ICreateObjectOptionsProps> =
    ({
         className,
         objectData,
         width,
         corpusWidth,
         service,
         onClose,
     }) => {

        const {t} = useTranslation();
        const [currentWidth, setCurrentWidth] = useState<number | undefined>(width && !isNaN(+width) ? + width : undefined);
        const [currentCorpusWidth, setCurrentCorpusWidth] = useState<number | undefined>(corpusWidth && !isNaN(+corpusWidth) ? + corpusWidth : undefined);
        const [createOptions, setNewOptions, setCreateOptions] = useCreateOptions(
            objectData,
            service,
            currentWidth,
            currentCorpusWidth
        );
        const [modulePrice, setModulePrice] = useState<IModulePriceData | undefined>();
        const [modulePriceLoading, setModulePriceLoading] = useState<boolean>(true);
        const [priceError, setPriceError] = useState<string>('');
        const [priceNote, setPriceNote] = useState<string>('');
        const dispatch = useDispatch();

        const handleOptionChange = (fieldId: string, value: any, groupId?: string): boolean => {
            if (fieldId === 'width') {
                switch (groupId) {
                    case 'corpus':
                        setCurrentCorpusWidth((value && !isNaN(+value) ? + value : undefined));
                        break;
                    case 'sizes':
                        setCurrentWidth((value && !isNaN(+value) ? + value : undefined));
                        break;
                }
            }
            return setNewOptions(fieldId, value, groupId);
        };

        const onCreateObject = () => {
            onClose(false);
            if (modulePriceLoading) {
                showMessage({
                    type: MESSAGE_TYPE_WARNING,
                    message: t('Загрузка цен')
                }, dispatch);
                return;
            }
            if (!modulePrice) {
                showMessage({
                    type: MESSAGE_TYPE_ERROR,
                    message: t('Не удалось посчитать цену модуля')
                }, dispatch);
                return;
            }
            if (objectData.builtIn) {
                onClose(false);
            } else {
                let objectCreateOptions: any = {...createOptions};
                service.setMultipleFacadeMaterialToCreateOptions(
                    objectData,
                    objectCreateOptions,
                    service.getFacadeMaterialById(createOptions.multipleFacadeMaterial)
                );
                service.setDefaultFacadeMaterialsToCreateOptions(objectData, objectCreateOptions);
                const createObject: ThreeUnit | TCreateObjectError = service.createDynamicCommonObject(
                    objectData.uid,
                    objectCreateOptions,
                    modulePrice,
                    objectData.catalogCode
                );
                if ('isError' in createObject) {
                    showMessage({
                        type: MESSAGE_TYPE_ERROR,
                        message: (createObject as TCreateObjectError).message
                    }, dispatch);
                    console.error('createDynamicCommonObject error!');
                } else {
                    onClose(false);
                }
            }

        }

        useEffectDidMount(() => {
            setPriceNote(t('Идет расчет...') || '')
            service.loadCreateObjectPrices(objectData).then(() => {
                setCreateOptions(service.getDefaultOptions(
                    objectData,
                    currentWidth,
                    currentCorpusWidth,
                    service.getFacadeMaterialById(createOptions.multipleFacadeMaterial)
                ));
                updateModulePrice();
            }).catch(() => {
                setPriceError(t('Не удалось получить цены с сервера') || '');
            });
        });

        const updateModulePrice = () => {
            let loadingOfferIds: string[];
            let newCreateOptions: any | undefined;

            newCreateOptions = service.setCreateUnitDynamicOptions(objectData, currentWidth || currentCorpusWidth, createOptions);
            if (newCreateOptions) {
                setCreateOptions(newCreateOptions);
            }
            const modulePrice: IModulePriceData = service.calculateCreateObjectPrice(
                objectData,
                createOptions,
                currentWidth || currentCorpusWidth
            );

            loadingOfferIds = service.getModuleLoadingOfferIds(modulePrice);
            setModulePriceLoading(loadingOfferIds.length > 0);
            setModulePrice(modulePrice);
            setPriceNote(modulePrice.note || '');
            if (modulePrice.errors.length > 0) {
                setPriceError(modulePrice.errors.map((error) => {return error.message}).join(' '));
            } else {
                setPriceError('');
            }
        };
        useEffect(updateModulePrice, [createOptions, objectData, service, currentWidth, currentCorpusWidth, setCreateOptions]);

        return (
            <div className={cnCreateObjectOptions({}, [className])}>
                <div className={cnCreateObjectOptions('Body')}>
                    <div className={cnCreateObjectOptions('Header')}>
                        <div className={cnCreateObjectOptions('Images')} onClick={onCreateObject}>
                            {
                                objectData.svgImage ?
                                    <Svg icon={objectData.svgImage}
                                         className={cnCreateObjectOptions('Icon')}
                                         label={t(objectData.title) || ''}/> :
                                    (
                                        objectData.image.length > 0 ?
                                            <img src={objectData.image} alt={t(objectData.title) || ''} className={cnCreateObjectOptions('Image')}/> :
                                            <Svg icon={'no-photo'}
                                                 className={cnCreateObjectOptions('Icon')}
                                                 label={t(objectData.title) || ''}/>
                                    )
                            }
                        </div>
                        <div className={cnCreateObjectOptions('NameContainer')}>
                            <div className={cnCreateObjectOptions('Name')}>
                                {t(objectData.title)}
                            </div>
                            {objectData.heightText.length > 0 || objectData.depthText.length > 0 ?
                                <div className={cnCreateObjectOptions('Sizes')}>
                                    {objectData.heightText.length > 0 ?
                                        <div className={cnCreateObjectOptions('SizesItem')}>
                                            <p className={cnCreateObjectOptions('SizesTitle')}>{t('Высота')}</p>
                                            <p className={cnCreateObjectOptions('SizesValue')}>{objectData.heightText}</p>
                                        </div> : null
                                    }
                                    {objectData.depthText.length > 0 ?
                                        <div className={cnCreateObjectOptions('SizesItem')}>
                                            <p className={cnCreateObjectOptions('SizesTitle')}>{t('Глубина')}</p>
                                            <p className={cnCreateObjectOptions('SizesValue')}>{objectData.depthText}</p>
                                        </div> : null
                                    }
                                </div> : null
                            }
                        </div>
                    </div>
                    <CreateObjectOptionsList objectData={objectData}
                                             createOptions={createOptions}
                                             handleOptionChange={handleOptionChange}
                                             service={service}
                    />

                    <CreateObjectOptionsPriceAmount objectData={objectData} service={service} modulePrice={modulePrice}/>
                    <div className={cnCreateObjectOptions('Footer')}>
                        {(modulePrice && !priceError) ?
                            <Button
                                className={cnCreateObjectOptions('Button')}
                                type={'button'}
                                // CHANGE CALLBACK HERE
                                text={t('Добавить модуль') || undefined}
                                leftIcon={{path: 'plus2', width: 16, height: 16, marginRight: 10}}
                                mods={{
                                    mods:{bkg: 'lightGreen', color: 'white', fill: 'white'}
                                }}
                                callBack={onCreateObject}
                                disabled={modulePriceLoading}
                            />
                            :
                            <div className={cnCreateObjectOptions('PriceError')}>{priceError ? t('Нельзя добавить модуль в проект') : ''}</div>
                        }
                    </div>
                    <div className={cnCreateObjectOptions('Notes')}>
                        <div className={cnCreateObjectOptions('Note')}>
                            {objectData.notPrice || t('* при заказе сегодня и оплате в течение 3-х суток')}
                            {priceNote}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
